import React, { useState, useEffect, useContext } from "react";
import {
  FaWallet,
  FaMapMarkerAlt,
  FaQuestionCircle,
  FaInfoCircle,
  FaSignOutAlt,
  FaShoppingBag,
  FaUser,
  FaBell,
} from "react-icons/fa";

import { PenLine, ChevronRight } from "lucide-react";

import Address from "./Address/Index";
import Wallet from "./Wallet/Wallet";
import FamilyMemberById from "./FamilyMember/FamilyMemberById";
import CreateFamilyMember from "./FamilyMember/CreateFamilyMember";
import MyOrders from "./Order/MyOrder";
import Wishlist from "./Wishlist/Wishlist";
import HelpSupport from "./HelpSupport/HelpSupport";

import UpdateProfileModal from "../../component/Navbar/UpdateProfileModal";
import LogoutPopup from "../../component/common/LogoutPopup";

// ✅ SIRF YEH CHANGE - CustomerAPI se import
// import { getCustomerById } from "../../services/Profile/CustomerAPI";
import { UserContext } from "../../context/UserContext";

import GetNotification from "./Notification/GetNotification";

export default function Profile() {
  const { user, logoutUser } = useContext(UserContext);

  const [activeSection, setActiveSection] = useState("profile");
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(false);

  const [showCreateFamilyForm, setShowCreateFamilyForm] = useState(false);
  const [editMemberData, setEditMemberData] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [refreshFamilyList, setRefreshFamilyList] = useState(false);

  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const userId = user?.id || user?.userId;

  // ✅ IMPORTANT: login type detect ONLY from real data
  const isEmailLogin = !!userData?.emailId && !userData?.phoneNumber;
  const isPhoneLogin = !!userData?.phoneNumber && !userData?.emailId;

  /* ================= FETCH PROFILE ================= */
  const fetchProfile = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await getCustomerById(userId);
      const finalData = res?.data ?? res;
      console.log("✅ PROFILE DATA 👉", finalData);
      setUserData(finalData || {});
    } catch (err) {
      console.log("❌ Profile fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    if (userId) fetchProfile();
  }, [userId]);

  /* ================= FIXED CONTEXT SYNC ================= */
  useEffect(() => {
    if (user && !userData?.customerName && !userData?.emailId && !userData?.phoneNumber) {
      setUserData(user);
    }
  }, [user]);

  /* ================= IMAGE ================= */
  const getImageUrl = () => {
    if (!userData?.photo) return null;
    if (userData.photo.startsWith("data:image")) return userData.photo;
    if (userData.photo.startsWith("http")) return `${userData.photo}?v=${Date.now()}`;
    return `https://admintest.medrphalabs.com/CustomerImage/${userData.photo}?v=${Date.now()}`;
  };

  /* ================= LOGOUT ================= */
  const confirmLogout = () => {
    logoutUser();
    setShowLogoutPopup(false);
    window.location.href = "/signin";
  };

  /* ================= RIGHT SECTION ================= */
  const renderSection = () => {
    switch (activeSection) {
      case "wallet":       return <Wallet />;
      case "address":      return <Address />;
      case "orders":       return <MyOrders />;
      case "wishlist":     return <Wishlist />;
      case "help_support": return <HelpSupport />;
      case "notifications":return <GetNotification />;

      case "family_member":
        return (
          <>
            <FamilyMemberById
              userId={userId}
              onAdd={() => setShowCreateFamilyForm(true)}
              onEdit={(data) => { setEditMemberData(data); setShowEditForm(true); }}
              refresh={refreshFamilyList}
            />
            {showCreateFamilyForm && (
              <CreateFamilyMember
                userId={userId}
                onClose={() => setShowCreateFamilyForm(false)}
                onSuccess={(updatedData) => { const finalData = updatedData?.data ?? updatedData; setUserData(finalData); }}
              />
            )}
            {showEditForm && (
              <CreateFamilyMember
                userId={userId}
                editData={editMemberData}
                onClose={() => setShowEditForm(false)}
                onSuccess={() => { setShowEditForm(false); setRefreshFamilyList(!refreshFamilyList); }}
              />
            )}
          </>
        );

      default:
        return (
          <div>
            {/* Personal Information Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-8 mb-5">
              <div className="flex justify-between items-center mb-8">
                <span className="text-xl font-bold text-gray-900">
                  Personal Information
                </span>
                <button
                  onClick={() => setShowProfileModal(true)}
                  className="text-red-600 font-semibold text-sm border border-red-600 rounded px-6 py-2 bg-transparent cursor-pointer hover:bg-red-50 transition-colors"
                >
                  Edit
                </button>
              </div>

              {loading ? (
                <div>
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-4 bg-gray-100 rounded mb-4 animate-pulse"
                      style={{ width: `${60 + i * 10}%` }}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <div className="text-xs text-gray-400 mb-2 font-semibold uppercase tracking-wide">
                      Full Name
                    </div>
                    <div className="text-base text-gray-900 font-medium">
                      {userData?.customerName || <span className="text-gray-300">Enter your name</span>}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-400 mb-2 font-semibold uppercase tracking-wide">
                      Mobile Number
                    </div>
                    <div className="text-base text-gray-900 font-medium">
                      {isEmailLogin ? (
                        <span className="text-gray-300">N/A</span>
                      ) : (
                        userData?.phoneNumber || <span className="text-gray-300">Add mobile number</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-400 mb-2 font-semibold uppercase tracking-wide">
                      Email Address
                    </div>
                    <div className="text-base text-gray-900 font-medium">
                      {isPhoneLogin ? (
                        <span className="text-gray-300">N/A</span>
                      ) : (
                        userData?.emailId || <span className="text-gray-300">Enter your email</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Info Tip Box */}
            <div className="bg-red-50 border border-red-200 rounded-lg px-7 py-5">
              <div className="text-sm text-red-600 font-semibold mb-2">
                ℹ️ Account Security Tip
              </div>
              <div className="text-sm text-gray-600 leading-relaxed">
                Keep your profile updated to enjoy faster checkouts and personalized recommendations.
              </div>
            </div>
          </div>
        );
    }
  };

  const menuItems = [
    { id: "profile",        text: "Profile Information", icon: <FaInfoCircle size={17} /> },
    { id: "wallet",         text: "My Wallet",           icon: <FaWallet size={17} /> },
    { id: "address",        text: "Manage Addresses",    icon: <FaMapMarkerAlt size={17} /> },
    { id: "family_member",  text: "Family Members",      icon: <FaUser size={17} /> },
    { id: "orders",         text: "My Orders",           icon: <FaShoppingBag size={17} /> },
    { id: "wishlist",       text: "Saved Items",         icon: <FaQuestionCircle size={17} /> },
    { id: "notifications",  text: "Notifications",       icon: <FaBell size={17} /> },
    { id: "help_support",   text: "Help & Support",      icon: <FaQuestionCircle size={17} /> },
  ];

  return (
    <div className="min-h-screen bg-gray-100 py-10 pb-16 font-sans">
      <div className="max-w-5xl mx-auto px-5">
        <div className="flex gap-5 items-start">

          {/* ===== LEFT SIDEBAR ===== */}
          <div className="w-72 min-w-[280px] flex-shrink-0 sticky top-20 self-start">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">

              {/* USER BLOCK */}
              <div className="flex items-center gap-4 px-6 py-7 border-b border-gray-100">
                {getImageUrl() ? (
                  <img
                    src={getImageUrl()}
                    className="w-16 h-16 rounded-full object-cover border-2 border-red-500 flex-shrink-0"
                    alt="profile"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                    {userData.customerName?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-400 mb-0.5">Hello,</div>
                  <div className="text-base font-bold text-gray-900 truncate">
                    {userData.customerName || "User"}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5 truncate">
                    {isPhoneLogin ? userData?.phoneNumber : userData?.emailId || ""}
                  </div>
                  <button
                    onClick={() => setShowProfileModal(true)}
                    className="flex items-center gap-1 text-red-600 text-xs font-semibold bg-transparent border-0 cursor-pointer p-0 mt-1.5 hover:text-red-700"
                  >
                    <PenLine size={12} /> Edit Profile
                  </button>
                </div>
              </div>

              {/* MENU */}
              <nav className="py-2">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={`w-full flex items-center gap-4 px-6 py-3.5 border-0 cursor-pointer text-left transition-all duration-100
                      ${activeSection === item.id
                        ? "bg-red-50 border-l-4 border-l-red-600"
                        : "bg-transparent border-l-4 border-l-transparent hover:bg-gray-50"
                      }`}
                  >
                    <span className={activeSection === item.id ? "text-red-600" : "text-gray-400"}>
                      {item.icon}
                    </span>
                    <div className="flex-1">
                      <div className={`text-sm ${activeSection === item.id ? "font-bold text-red-600" : "font-medium text-gray-800"}`}>
                        {item.text}
                      </div>
                    </div>
                    <ChevronRight size={14} className={activeSection === item.id ? "text-red-500" : "text-gray-300"} />
                  </button>
                ))}
              </nav>

              {/* LOGOUT */}
              <div className="border-t border-gray-100 px-6 py-4">
                <button
                  onClick={() => setShowLogoutPopup(true)}
                  className="flex items-center gap-4 bg-transparent border-0 cursor-pointer w-full py-2 hover:opacity-75 transition-opacity"
                >
                  <FaSignOutAlt size={17} className="text-gray-400" />
                  <span className="text-sm font-semibold text-gray-800">Logout</span>
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT CONTENT */}
          <div className="flex-1 min-w-0">
            {renderSection()}
          </div>
        </div>
      </div>

      {/* ===== MODALS ===== */}
      <UpdateProfileModal
        show={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onSuccess={fetchProfile}
      />

      <LogoutPopup
        isOpen={showLogoutPopup}
        onCancel={() => setShowLogoutPopup(false)}
        onConfirm={confirmLogout}
      />
    </div>
  );
}